// Setup › Your plan and data on the SettingsPage pattern (BL-189): the company's subscription with
// Flowology (our billing, through the provider's hosted checkout: GET /billing), and downloading all of
// its data (GET /export). As Company.tsx's SubscriptionPart and DataPart draw them in Blueprint, with
// nothing changed: the subscription is the owner's (billing.manage), the download data.export's. The
// provider's checkout and the banner's Pay now come back here (#billing). Riverstone keeps Company's
// Subscription and Your data parts.
import { useCallback, useEffect, useState } from 'react';
import { RpcError } from '@flowology/client';
import { Button, KeyValue } from '@flowology/ui-web/fl';
import { SettingsPage, SettingsSection } from '@flowology/ui-web/patterns';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { money, subscriptionLine, type Plan, type Subscription } from './account-model.ts';
import { billing } from './billing.ts';

const LEDE = 'Your plan with Flowology, and everything your company has here, yours to download.';
const message = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'Something went wrong.';

export function YourPlanPage({ nav }: { nav: NavProps }) {
  // by the permissions the person holds, whichever role gives them (QA-681): both stay with the Owner role
  const held = nav.permissions ?? [];
  const crumb = usePageCrumb(nav);
  return (
    <div className="app">
      {/* the sheet pads the page (blueprint.css .bp-sheet), as PageFrame does; no .page padding on top */}
      <main>
        <PageBanners nav={nav} />
        <SettingsPage head={{ ...(crumb ? { crumb } : {}), title: 'Your plan and data', lede: LEDE }}>
          {held.includes('billing.manage') && <SubscriptionSection />}
          {held.includes('data.export') && <DataSection />}
        </SettingsPage>
      </main>
    </div>
  );
}

function SubscriptionSection() {
  const [data, setData] = useState<{ subscription: Subscription | null; plan: Plan | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const load = useCallback(() => {
    void billing<{ subscription: Subscription | null; plan: Plan | null }>('GET', '/billing')
      .then((r) => {
        setData(r);
        setError(null);
      })
      .catch((e: unknown) => setError(message(e)));
  }, []);
  useEffect(load, [load]);
  if (!data)
    return (
      <SettingsSection
        title="Subscription"
        status={error ? 'failed' : 'loading'}
        {...(error ? { failedText: error } : {})}
        onRetry={load}
      />
    );
  const { subscription: sub, plan } = data;
  if (!sub)
    return (
      <SettingsSection
        title="Subscription"
        status="empty"
        emptyText="Your company has no subscription with us to manage here."
        foot="Nothing to do: your plan is arranged with Flowology directly. To change it, the owner asks us through Help."
      />
    );
  const pay = () => {
    setBusy(true);
    setError(null);
    void billing<{ url: string }>('POST', '/billing/checkout')
      .then((r) => location.assign(r.url))
      .catch((e: unknown) => {
        setError(message(e));
        setBusy(false);
      });
  };
  const cancel = () => {
    if (
      !confirm('Cancel the subscription? Your company becomes read-only after the paid time and the grace.')
    )
      return;
    setBusy(true);
    void billing('POST', '/billing/cancel')
      .then(load)
      .catch((e: unknown) => setError(message(e)))
      .finally(() => setBusy(false));
  };
  const paying = !!sub.providerSubscriptionId && sub.status !== 'canceled';
  return (
    <SettingsSection
      title="Subscription"
      line={subscriptionLine(sub, timeZone)}
      action={
        paying ? (
          <Button isDisabled={busy} onPress={cancel}>
            Cancel the subscription
          </Button>
        ) : (
          <Button variant="primary" isDisabled={busy} onPress={pay}>
            {sub.status === 'trialing' ? 'Add a card' : 'Pay now'}
          </Button>
        )
      }
      {...(error ? { note: <span role="alert">{error}</span> } : {})}
      foot="Cards are handled by our payment provider; we never see the card number."
    >
      {plan && (
        <KeyValue
          label="Your plan"
          items={[
            { label: 'Plan', value: plan.name },
            {
              label: 'Price',
              value: `${money(plan.priceCents, plan.currency)} a month, ${plan.seatsIncluded} people included${
                plan.seatPriceCents > 0 ? `, then ${money(plan.seatPriceCents, plan.currency)} a person` : ''
              }`,
            },
          ]}
        />
      )}
    </SettingsSection>
  );
}

function DataSection() {
  return (
    <SettingsSection
      title="Your data"
      line="Download everything your company has in Flowology: customers, jobs, estimates, invoices, payments, the books and the history. One spreadsheet file (CSV) per kind of record, in a ZIP. You can do this any time, also if your subscription ends."
      action={
        // the route answers with the ZIP as an attachment: the page stays where it is
        <Button variant="primary" onPress={() => location.assign('/export')}>
          Download all your data
        </Button>
      }
      foot="Photos, signatures and PDFs are listed by name; ask us for a copy of the files themselves."
    />
  );
}
